import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Download,
  Delete,
  CalendarMonth,
} from '@mui/icons-material';
import {
  EnhancedProjectFile,
  formatFileSize,
  CATEGORY_COLORS,
} from '../../types/file.types';

interface FileTimelineViewProps {
  files: EnhancedProjectFile[];
  selectedFiles: string[];
  onFileSelect: (fileId: string, selected: boolean) => void;
  onFileClick: (file: EnhancedProjectFile) => void;
  onFileDownload?: (file: EnhancedProjectFile) => void;
  onFileDelete?: (file: EnhancedProjectFile) => void;
  searchTerm?: string;
}

const FileTimelineView: React.FC<FileTimelineViewProps> = ({
  files,
  selectedFiles,
  onFileSelect,
  onFileClick,
  onFileDownload,
  onFileDelete,
  searchTerm = '',
}) => {
  // 검색 필터링
  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) {
      return files;
    }

    const lowerSearch = searchTerm.toLowerCase();
    return files.filter((file) => {
      const fileName = (file.fileName || '').toLowerCase();
      const description = (file.description || '').toLowerCase();
      const tags = (file.tags || []).map((tag: string) => tag.toLowerCase());
      const searchKeywords = (file.searchKeywords || []).map((kw: string) =>
        kw.toLowerCase()
      );

      return (
        fileName.includes(lowerSearch) ||
        description.includes(lowerSearch) ||
        tags.some((tag: string) => tag.includes(lowerSearch)) ||
        searchKeywords.some((kw: string) => kw.includes(lowerSearch))
      );
    });
  }, [files, searchTerm]);

  // 빈 상태 처리
  if (filteredFiles.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
      >
        <CalendarMonth sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {searchTerm
            ? '검색 결과가 없습니다'
            : '파일이 없습니다'}
        </Typography>
      </Box>
    );
  }

  // 날짜별 그룹화
  const groupedFiles = useMemo(() => {
    const groups: { [dateKey: string]: EnhancedProjectFile[] } = {};

    filteredFiles.forEach((file) => {
      if (!file.uploadedAt) return;

      const date =
        typeof file.uploadedAt === 'object' && 'toDate' in file.uploadedAt
          ? file.uploadedAt.toDate()
          : new Date(file.uploadedAt);

      const dateKey = date.toLocaleDateString('ko-KR');

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(file);
    });

    return groups;
  }, [filteredFiles]);

  // 날짜 정렬 (최신순)
  const sortedDateKeys = Object.keys(groupedFiles).sort((a, b) => {
    const dateA = new Date(a.split('. ').reverse().join('-'));
    const dateB = new Date(b.split('. ').reverse().join('-'));
    return dateB.getTime() - dateA.getTime();
  });

  // TODO: Timeline UI 렌더링 (Step 7-3)

  return (
    <Box>
      {sortedDateKeys.map((dateKey) => {
        const dateFiles = groupedFiles[dateKey];

        return (
          <Box key={dateKey} mb={4}>
            {/* 날짜 헤더 */}
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'primary.main',
                borderBottom: 2,
                borderColor: 'primary.main',
                pb: 1,
                mb: 2,
              }}
            >
              <CalendarMonth />
              {dateKey}
              <Typography variant="body2" color="text.secondary" ml="auto">
                {dateFiles.length}개 파일
              </Typography>
            </Typography>

            {/* 파일 리스트 */}
            <Box display="flex" flexDirection="column" gap={1} pl={2}>
              {dateFiles.map((file) => {
                const isSelected = selectedFiles.includes(file.id || '');

                return (
                  <Box
                    key={file.id || ''}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    p={1.5}
                    sx={{
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.light',
                      },
                    }}
                    onClick={() => onFileClick(file)}
                  >
                    {/* 체크박스 */}
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        onFileSelect(file.id || '', e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />

                    {/* 카테고리 Chip */}
                    <Chip
                      label={file.category || '기타'}
                      size="small"
                      sx={{
                        bgcolor: CATEGORY_COLORS[file.category] || '#757575',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 60,
                      }}
                    />

                    {/* 파일명 */}
                    <Typography
                      variant="body1"
                      sx={{
                        flex: 1,
                        fontWeight: 'medium',
                      }}
                    >
                      {file.fileName}
                    </Typography>

                    {/* 크기 */}
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(file.fileSize)}
                    </Typography>

                    {/* 액션 버튼 */}
                    <Box display="flex" gap={0.5}>
                      {onFileDownload && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileDownload(file);
                          }}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.light' },
                          }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      )}
                      {onFileDelete && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileDelete(file);
                          }}
                          sx={{
                            color: 'error.main',
                            '&:hover': { bgcolor: 'error.light' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default FileTimelineView;
